import { type ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import {
  LayoutDashboard,
  CheckSquare,
  StickyNote,
  Folders,
  AppWindow,
  LogOut,
  Settings as SettingsIcon,
  ShoppingCart,
  Sparkles,
  Sun,
  Moon,
} from 'lucide-react';
import { useLogout, useMe } from '../lib/hooks';
import { useT } from '../lib/i18n';
import { useTheme } from '../lib/theme';

type NavItem = {
  href: string;
  label: string;
  icon: typeof LayoutDashboard;
  accent: string;
};

export function Shell({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const t = useT();
  const me = useMe();
  const logout = useLogout();
  const { theme, toggle } = useTheme();

  const nav: NavItem[] = [
    {
      href: '/',
      label: t('nav.dashboard'),
      icon: LayoutDashboard,
      accent: 'text-indigo-600 dark:text-indigo-300',
    },
    {
      href: '/tasks',
      label: t('nav.tasks'),
      icon: CheckSquare,
      accent: 'text-emerald-600 dark:text-emerald-300',
    },
    {
      href: '/notes',
      label: t('nav.notes'),
      icon: StickyNote,
      accent: 'text-amber-600 dark:text-amber-300',
    },
    {
      href: '/areas',
      label: t('nav.areas'),
      icon: Folders,
      accent: 'text-fuchsia-600 dark:text-fuchsia-300',
    },
    {
      href: '/workspaces',
      label: t('nav.workspaces'),
      icon: AppWindow,
      accent: 'text-cyan-600 dark:text-cyan-300',
    },
    {
      href: '/shopping',
      label: 'Shopping',
      icon: ShoppingCart,
      accent: 'text-rose-600 dark:text-rose-300',
    },
    {
      href: '/settings',
      label: 'Cài đặt',
      icon: SettingsIcon,
      accent: 'text-slate-600 dark:text-slate-300',
    },
  ];

  const initial = me.data?.name?.[0]?.toUpperCase() ?? '?';

  return (
    <div className="flex h-full">
      <aside className="flex w-64 flex-col border-r border-slate-200 bg-white/70 backdrop-blur-xl dark:border-white/5 dark:bg-gradient-to-b dark:from-white/[0.02] dark:to-transparent">
        {/* Brand */}
        <div className="px-5 pt-6 pb-5">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 shadow-lg shadow-indigo-500/30">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="text-base font-bold tracking-tight text-slate-900 dark:text-white">
                Personal OS
              </div>
              <div className="text-[11px] tracking-wider text-slate-500 uppercase">Cockpit</div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 space-y-1 px-3">
          {nav.map((item) => {
            const active = location === item.href;
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                href={item.href}
                className={active ? 'nav-link-active' : 'nav-link'}
              >
                <Icon className={`h-4 w-4 ${active ? item.accent : ''}`} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User + theme toggle */}
        <div className="space-y-1 border-t border-slate-200 p-3 dark:border-white/5">
          <div className="flex items-center gap-3 rounded-xl px-3 py-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-sm font-bold text-white">
              {initial}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                {me.data?.name ?? '…'}
              </div>
              <div className="truncate text-[11px] text-slate-500">{me.data?.email ?? ''}</div>
            </div>
          </div>

          <button
            type="button"
            onClick={toggle}
            className="nav-link w-full"
            title={theme === 'light' ? 'Chuyển sang Dark mode' : 'Chuyển sang Light mode'}
          >
            {theme === 'light' ? (
              <>
                <Moon className="h-4 w-4" />
                Dark mode
              </>
            ) : (
              <>
                <Sun className="h-4 w-4 text-amber-300" />
                Light mode
              </>
            )}
          </button>

          <button type="button" onClick={() => logout.mutate()} className="nav-link w-full">
            <LogOut className="h-4 w-4" />
            {t('nav.logout')}
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">{children}</main>
    </div>
  );
}
